import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateNotification } from "../api/notification.api";
import { useResultHandler } from "../../../hooks/useResultHandler";

export const useUpdateNotificationPin = () => {
  const { t } = useTranslation();
  const { handleResult } = useResultHandler();
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);

  const updateNotificationPin = useCallback(
    async (id: string, isPinned: boolean) => {
      try {
        setUpdatingIds((current) => new Set(current).add(id));
        setError(null);
        const response = await updateNotification(id, { is_pinned: isPinned });
        const isSuccess = handleResult(response, {
          successMessage: t("notification.notification_updated_successfully"),
        });
        if (!isSuccess) {
          throw new Error(
            response.user_message || response.message || "Failed to update notification",
          );
        }

        return response;
      } catch (updateError) {
        const message =
          updateError instanceof Error ? updateError.message : "Failed to update notification";
        setError(message);
        throw updateError;
      } finally {
        setUpdatingIds((current) => {
          const next = new Set(current);
          next.delete(id);
          return next;
        });
      }
    },
    [handleResult, t],
  );

  return { updateNotificationPin, updatingIds, error };
};
